import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { FormattedMessage } from '../../../../util/reactIntl';
import { NamedLink } from '../../../../components';

import css from './SectionForProviders.module.css';

const SECTION_ID = 'for-providers';
// How long to keep the section pinned while the content above it settles.
const SETTLE_MS = 2500;

/**
 * Arriving via "Providers" (/#for-providers): Routes.js scrolls to the section right away, but the
 * class rows above it are still loading -- they then grow and push the section down, leaving the
 * visitor at the parents' section (staging). While the page settles, re-align to the section on
 * every layout change; stop after SETTLE_MS or as soon as the visitor scrolls/taps themselves.
 * location.key re-arms it when the link is clicked again on the homepage.
 */
const useKeepSectionInView = () => {
  const location = useLocation();
  useEffect(() => {
    if (location.hash !== `#${SECTION_ID}` || typeof window === 'undefined') {
      return undefined;
    }
    const el = document.getElementById(SECTION_ID);
    if (!el || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    let done = false;
    const align = () => {
      if (!done) {
        el.scrollIntoView({ block: 'start' });
      }
    };
    const observer = new ResizeObserver(align);
    observer.observe(document.body);
    const stop = () => {
      done = true;
      observer.disconnect();
      ['wheel', 'touchstart', 'keydown', 'mousedown'].forEach(evt =>
        window.removeEventListener(evt, stop)
      );
    };
    ['wheel', 'touchstart', 'keydown', 'mousedown'].forEach(evt =>
      window.addEventListener(evt, stop, { passive: true })
    );
    const timer = window.setTimeout(stop, SETTLE_MS);
    return () => {
      window.clearTimeout(timer);
      stop();
    };
  }, [location.hash, location.key]);
};

const STEPS = ['list', 'manage', 'grow'];
// No fees to join first, and they can leave anytime. Account deletion is blocked only while booked
// classes or payouts are still in progress -- hence the qualifier. ("Zero risk" was dropped:
// providers still bear the Stripe fee on a refunded booking.)
const FACTS = ['risk', 'fee', 'payouts', 'policy', 'leave'];

/**
 * Signed-out homepage: the invitation to providers -- three numbered steps (list, manage, grow),
 * the key facts (no fees to join, pay only when booked, Stripe payouts, one cancellation policy, delete
 * the account anytime), the Founding Member offer, "List your activity" (provider sign-up) and a
 * log-in link for businesses already listing. No fee percentages here on purpose (client ask) --
 * the rates live in the provider FAQ and Provider Agreement. The anchor id lets a menu link jump
 * straight here. Static content only (SSR-safe).
 *
 * The Founding Member offer ("the first 50") isn't counted by the app -- admins grant the status --
 * so remove the box (SectionForProviders.founding.*) once the program is full.
 *
 * @returns {JSX.Element}
 */
const SectionForProviders = () => {
  useKeepSectionInView();
  return (
    <section id={SECTION_ID} className={css.root} aria-labelledby="for-providers-title">
      <div className={css.band}>
        <span className={css.eyebrow}>
          <FormattedMessage id="SectionForProviders.eyebrow" />
        </span>
        <h2 id="for-providers-title" className={css.title}>
          <FormattedMessage id="SectionForProviders.title" />
        </h2>
        <p className={css.subtitle}>
          <FormattedMessage id="SectionForProviders.subtitle" />
        </p>

        <ol className={css.steps}>
          {STEPS.map((key, i) => (
            <li key={key} className={css.step}>
              <span className={css.number} aria-hidden="true">
                {i + 1}
              </span>
              <div>
                <h3 className={css.stepTitle}>
                  <FormattedMessage id={`SectionForProviders.${key}.title`} />
                </h3>
                <p className={css.stepBody}>
                  <FormattedMessage id={`SectionForProviders.${key}.body`} />
                </p>
              </div>
            </li>
          ))}
        </ol>

        <ul className={css.facts}>
          {FACTS.map(key => (
            <li key={key} className={css.fact}>
              <span className={css.check} aria-hidden="true">
                ✓
              </span>
              <FormattedMessage id={`SectionForProviders.fact.${key}`} />
            </li>
          ))}
        </ul>

        <p className={css.founding}>
          <span className={css.foundingBadge}>
            <FormattedMessage id="SectionForProviders.founding.badge" />
          </span>
          <span>
            <FormattedMessage id="SectionForProviders.founding.body" />
          </span>
        </p>

        <div className={css.actions}>
          <NamedLink
            name="SignupForUserTypePage"
            params={{ userType: 'provider' }}
            className={css.primaryAction}
          >
            <FormattedMessage id="SectionForProviders.cta" />
          </NamedLink>
          {/* Businesses already on KidHive: the one shared login (no separate business login). */}
          <span className={css.loginLine}>
            <FormattedMessage id="SectionForProviders.loginPrompt" />{' '}
            <NamedLink name="LoginPage" className={css.loginLink}>
              <FormattedMessage id="SectionForProviders.loginLink" />
            </NamedLink>
          </span>
          <NamedLink name="FaqPage" params={{ userType: 'provider' }} className={css.faqLink}>
            <FormattedMessage id="SectionForProviders.faq" />
          </NamedLink>
        </div>
      </div>
    </section>
  );
};

export default SectionForProviders;
